import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { LINK_FILTER_LABEL } from '@/lib/experience-labels';
import { EMPTY_SELECT_VALUE, fromSelectValue, toSelectValue } from '@/lib/select-value';
import type { ExperienceFilters } from '@matter_extractor/shared';
import { experienceLinkFilterSchema } from '@matter_extractor/shared';
import { Select } from '@pms-digital/design-system';

/**
 * The three own filters of an experience (refonte modulaire §15; maquette
 * `Main.dc.html`).
 *
 * Two of them - the protocol and the persona - sit in the ALWAYS-VISIBLE row,
 * in the two slots the layer keeps for its referent filters, and the third
 * sits in « Plus de filtres » with the common ones. That split is the mockup's
 * and it is argued in `filter-bar.tsx` at `renderReferents`: an experience's
 * referents are the links of §5.1, so « Protocole : nulle part » and an
 * « Expérience » select would both be filters on a relation this screen cannot
 * have - while the two references it CARRIES are exactly what §15 names
 * visible.
 *
 * ## The options come from the CARDS, and they are names
 *
 * `experienceCardSchema` names the protocol and the persona of each experience
 * and carries neither id, so the value of these two selects is a NAME and the
 * option list is the distinct names the library holds. The consequence - two
 * protocols of one name conflated - is named in `experience-filters.ts`, where
 * the comparison lives.
 *
 * A select whose options are the library's own names rather than « Tous » and
 * « Nulle part » is also what makes them useful on day one: the referent
 * selects of the layer admit they have two states because no route serves their
 * list; here the list is the list already on screen.
 *
 * The three are the package `Select` (Radix, not native): the empty choice
 * « Tous » crosses it as `EMPTY_SELECT_VALUE`, and the bleu nuit border of an
 * ARMED filter is gone with `filterControlClass` - the package draws no such
 * state, so the chips under the bar are what say which filters are on.
 */

export interface ExperienceReferenceFiltersProps {
  filters: ExperienceFilters;
  /** The distinct names the library holds, sorted - `experienceReferenceOptions`. */
  options: { readonly protocols: readonly string[]; readonly personas: readonly string[] };
  onChange: (patch: Partial<ExperienceFilters>) => void;
}

export function ExperienceReferenceFilters({
  filters,
  options,
  onChange,
}: ExperienceReferenceFiltersProps) {
  return (
    <>
      {/* The test id sits on a wrapper: the package `Select` takes none (its
          trigger is the `combobox` inside). */}
      <div data-testid={EXPERIENCE_IDS.filterProtocol} className="w-45 max-w-full">
        <Select
          snug
          label="Protocole"
          value={toSelectValue(filters.referencedProtocolId ?? '')}
          onValueChange={(value) => {
            const name = fromSelectValue(value);
            onChange({ referencedProtocolId: name === '' ? null : name });
          }}
          options={[
            { value: EMPTY_SELECT_VALUE, label: 'Tous' },
            ...options.protocols.map((name) => ({ value: name, label: name })),
          ]}
        />
      </div>

      <div data-testid={EXPERIENCE_IDS.filterPersona} className="w-45 max-w-full">
        <Select
          snug
          label="Persona"
          value={toSelectValue(filters.referencedPersonaId ?? '')}
          onValueChange={(value) => {
            const name = fromSelectValue(value);
            onChange({ referencedPersonaId: name === '' ? null : name });
          }}
          options={[
            { value: EMPTY_SELECT_VALUE, label: 'Tous' },
            ...options.personas.map((name) => ({ value: name, label: name })),
          ]}
        />
      </div>
    </>
  );
}

export interface ExperienceLinkFilterFieldProps {
  filters: ExperienceFilters;
  onChange: (patch: Partial<ExperienceFilters>) => void;
}

/** « Lien actif » - the own filter of the « Plus de filtres » panel (§15). */
export function ExperienceLinkFilterField({ filters, onChange }: ExperienceLinkFilterFieldProps) {
  return (
    <div data-testid={EXPERIENCE_IDS.filterLink} className="w-45 max-w-full">
      <Select
        snug
        label="Lien actif"
        value={filters.activeLink}
        onValueChange={(value) => {
          const parsed = experienceLinkFilterSchema.safeParse(value);
          if (parsed.success) onChange({ activeLink: parsed.data });
        }}
        options={experienceLinkFilterSchema.options.map((option) => ({
          value: option,
          label: LINK_FILTER_LABEL[option],
        }))}
      />
    </div>
  );
}
